/**
 * AI 문서 아카이브 — 전자서명
 * Phase 1 범위: 양식(필드 목록) 등록·재사용 + 서명요청 링크 생성·상태 관리.
 * 좌표 기반 서명영역 편집기, 실제 발송(카카오 알림톡/메일), 서명 페이지 자체는 BE 연동 후 구현.
 *
 * 외부 서명은 선택 기능이 아니라 핵심 요구다(계약서·납품확인서·청렴이행각서·보안서약서 등을
 * 계약 상대방에게 보내 자필서명을 받는 용도) — 원본 GAS 설계의 Google OAuth보다 가벼운
 * "추측 불가능한 링크 토큰 + 접속 시 최소 본인확인"을 전제로 한다.
 * 설계: docs/agent-plans/CM/20260918-전자서명시스템-이식분석.md
 *       docs/agent-plans/CM/20260923-AI문서아카이브-OCR전자서명계약인사문서-통합설계분석.md §4.2
 */
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import useTenantCode from 'hooks/useTenantCode';
import { PageContainer, ActionButton } from 'styles/common.styles';
import { showAlert, showConfirm } from 'utils/toastBridge';
import {
  SIGN_FIELD_TYPES,
  SIGNATURE_DOC_PRESETS,
  loadTemplates,
  saveTemplate,
  removeTemplate,
  emptyTemplate,
  loadRequests,
  saveRequest,
  emptyRequest,
  buildSignLinkToken,
  createUid,
} from 'archive/signatureStore';

const Wrap = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 360px) 1fr;
  gap: 12px;
  align-items: start;
  font-size: 12px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
  .box { border: 1px solid #d9dde3; border-radius: 4px; background: #fff; padding: 8px 10px; display: flex; flex-direction: column; gap: 8px; }
  .title { font-weight: 700; font-size: 13px; }
  .row { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
  .hint { color: #5b6472; }
  input[type='text'], select {
    height: 24px; font-size: 12px; border: 1px solid #c5cad3; border-radius: 3px; padding: 0 4px;
  }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #d9dde3; padding: 2px 4px; }
  th { background: #f3f5f8; }
  code { background: #f3f5f8; padding: 1px 4px; border-radius: 3px; word-break: break-all; }
`;

const STATUS_LABEL = { DRAFT: '작성중', SENT: '발송대기', VIEWED: '열람됨', SIGNED: '서명완료', EXPIRED: '만료' };

const ArchiveSignature = () => {
  const tenantCode = useTenantCode();
  const [templates, setTemplates] = useState(() => loadTemplates(tenantCode));
  const [requests, setRequests] = useState(() => loadRequests(tenantCode));
  const [draft, setDraft] = useState(() => emptyTemplate());
  const [reqTemplateUid, setReqTemplateUid] = useState('');
  const [counterpartyName, setCounterpartyName] = useState('');
  const [counterpartyContact, setCounterpartyContact] = useState('');

  useEffect(() => {
    setTemplates(loadTemplates(tenantCode));
    setRequests(loadRequests(tenantCode));
  }, [tenantCode]);

  useEffect(() => {
    if (!reqTemplateUid && templates.length) setReqTemplateUid(templates[0].templateUid);
  }, [templates, reqTemplateUid]);

  const readyTemplates = useMemo(() => templates.filter((t) => t.status === 'ready'), [templates]);

  const addField = () => {
    setDraft((d) => ({
      ...d,
      fields: [...d.fields, { fieldUid: createUid('EF'), label: '', type: 'TEXT' }],
    }));
  };
  const setFieldAt = (fieldUid, patch) => {
    setDraft((d) => ({
      ...d,
      fields: d.fields.map((f) => (f.fieldUid === fieldUid ? { ...f, ...patch } : f)),
    }));
  };
  const removeFieldAt = (fieldUid) => {
    setDraft((d) => ({ ...d, fields: d.fields.filter((f) => f.fieldUid !== fieldUid) }));
  };

  const saveDraftTemplate = (status) => {
    if (!String(draft.templateName || '').trim()) {
      showAlert('양식명을 입력하세요.', 'warning');
      return;
    }
    if (!draft.fields.some((f) => f.type === 'SIGNATURE')) {
      showAlert('서명 필드가 최소 1개 필요합니다.', 'warning');
      return;
    }
    const next = { ...draft, status };
    saveTemplate(tenantCode, next);
    setTemplates(loadTemplates(tenantCode));
    setDraft(emptyTemplate());
    showAlert(status === 'ready' ? '양식을 저장하고 사용 가능 상태로 전환했습니다. (같은 테넌트 내 공유)' : '초안으로 저장했습니다.', 'success');
  };

  const editTemplate = (tpl) => setDraft({ ...tpl });

  const delTemplate = async (tpl) => {
    const ok = await showConfirm(`양식 "${tpl.templateName}"을 삭제할까요?`);
    if (!ok) return;
    removeTemplate(tenantCode, tpl.templateUid);
    setTemplates(loadTemplates(tenantCode));
  };

  const createRequest = () => {
    if (!reqTemplateUid) {
      showAlert('먼저 양식을 선택하세요.', 'warning');
      return;
    }
    if (!counterpartyName.trim()) {
      showAlert('상대방(서명자) 이름을 입력하세요.', 'warning');
      return;
    }
    const linkToken = buildSignLinkToken();
    const req = emptyRequest(reqTemplateUid, {
      counterpartyName: counterpartyName.trim(),
      counterpartyContact: counterpartyContact.trim(),
      status: 'SENT',
      linkToken,
      sentAt: new Date().toISOString(),
    });
    saveRequest(tenantCode, req);
    setRequests(loadRequests(tenantCode));
    setCounterpartyName('');
    setCounterpartyContact('');
    showAlert('서명 링크를 생성했습니다. 발송(카카오·메일)은 BE 연동 후 자동화됩니다.', 'success');
  };

  const signLink = (token) => `${window.location.origin}/sign/${token}`;

  const copyLink = async (token) => {
    try {
      await navigator.clipboard.writeText(signLink(token));
      showAlert('링크를 복사했습니다.', 'success');
    } catch {
      showAlert(signLink(token), 'info');
    }
  };

  const markSigned = (req) => {
    saveRequest(tenantCode, { ...req, status: 'SIGNED', signedAt: new Date().toISOString() });
    setRequests(loadRequests(tenantCode));
  };

  return (
    <PageContainer>
      <Wrap>
        <div className="box">
          <div className="title">1. 양식 등록/수정</div>
          <div className="row">
            <label>양식명 <input type="text" style={{ width: 180 }} value={draft.templateName} onChange={(e) => setDraft({ ...draft, templateName: e.target.value })} /></label>
          </div>
          <div className="row">
            <label>
              문서종류{' '}
              <select value={draft.docPreset} onChange={(e) => setDraft({ ...draft, docPreset: e.target.value })}>
                {SIGNATURE_DOC_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </label>
          </div>
          <table>
            <thead><tr><th>필드명</th><th>타입</th><th /></tr></thead>
            <tbody>
              {draft.fields.map((f) => (
                <tr key={f.fieldUid}>
                  <td><input type="text" style={{ width: '100%' }} value={f.label} onChange={(e) => setFieldAt(f.fieldUid, { label: e.target.value })} /></td>
                  <td>
                    <select value={f.type} onChange={(e) => setFieldAt(f.fieldUid, { type: e.target.value })}>
                      {SIGN_FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </td>
                  <td><ActionButton type="button" onClick={() => removeFieldAt(f.fieldUid)}>✕</ActionButton></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="row">
            <ActionButton type="button" onClick={addField}>＋ 필드 추가</ActionButton>
          </div>
          <span className="hint">서명 외 직급·회사명·연락처·주소 등 문서마다 다른 항목을 필드로 추가하세요.</span>
          <div className="row">
            <ActionButton type="button" onClick={() => saveDraftTemplate('draft')}>초안 저장</ActionButton>
            <ActionButton type="button" onClick={() => saveDraftTemplate('ready')}>저장 · 사용가능</ActionButton>
            {draft.templateUid && <ActionButton type="button" onClick={() => setDraft(emptyTemplate())}>새로 작성</ActionButton>}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="box">
            <div className="title">2. 저장된 양식 ({templates.length}) — 같은 세무법인/기관 내 공유</div>
            <table>
              <thead><tr><th>양식명</th><th>문서종류</th><th>필드</th><th>상태</th><th /></tr></thead>
              <tbody>
                {templates.length === 0 && <tr><td colSpan={5} className="hint">등록된 양식이 없습니다.</td></tr>}
                {templates.map((t) => (
                  <tr key={t.templateUid}>
                    <td>{t.templateName}</td>
                    <td>{SIGNATURE_DOC_PRESETS.find((p) => p.value === t.docPreset)?.label || t.docPreset}</td>
                    <td>{t.fields.map((f) => f.label).filter(Boolean).join(', ') || '-'}</td>
                    <td>{t.status === 'ready' ? '사용가능' : '초안'}</td>
                    <td>
                      <ActionButton type="button" onClick={() => editTemplate(t)}>수정</ActionButton>
                      <ActionButton type="button" onClick={() => delTemplate(t)}>삭제</ActionButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="box">
            <div className="title">3. 서명요청 발송 (외부 서명)</div>
            <div className="row">
              <label>
                양식{' '}
                <select value={reqTemplateUid} onChange={(e) => setReqTemplateUid(e.target.value)}>
                  <option value="">선택</option>
                  {readyTemplates.map((t) => <option key={t.templateUid} value={t.templateUid}>{t.templateName}</option>)}
                </select>
              </label>
              <label>상대방(업체·성명) <input type="text" style={{ width: 140 }} value={counterpartyName} onChange={(e) => setCounterpartyName(e.target.value)} /></label>
              <label>연락처/이메일 <input type="text" style={{ width: 140 }} value={counterpartyContact} onChange={(e) => setCounterpartyContact(e.target.value)} /></label>
              <ActionButton type="button" onClick={createRequest}>🔗 서명 링크 생성</ActionButton>
            </div>
            {readyTemplates.length === 0 && <span className="hint">먼저 "사용가능" 상태의 양식을 저장하세요.</span>}
          </div>

          <div className="box">
            <div className="title">4. 서명요청 현황 ({requests.length})</div>
            <table>
              <thead><tr><th>양식</th><th>상대방</th><th>상태</th><th>링크</th><th /></tr></thead>
              <tbody>
                {requests.length === 0 && <tr><td colSpan={5} className="hint">서명요청이 없습니다.</td></tr>}
                {requests.map((r) => {
                  const tplName = templates.find((t) => t.templateUid === r.templateUid)?.templateName || '-';
                  return (
                    <tr key={r.requestUid}>
                      <td>{tplName}</td>
                      <td>{r.counterpartyName}<br /><span className="hint">{r.counterpartyContact}</span></td>
                      <td>{STATUS_LABEL[r.status] || r.status}</td>
                      <td><code>{signLink(r.linkToken)}</code></td>
                      <td>
                        <ActionButton type="button" onClick={() => copyLink(r.linkToken)}>복사</ActionButton>
                        {r.status !== 'SIGNED' && (
                          <ActionButton type="button" onClick={() => markSigned(r)} title="서명 페이지 연동 전 테스트용">
                            서명완료 처리(테스트)
                          </ActionButton>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <span className="hint">실제 발송(카카오 알림톡/메일)과 서명 페이지, 본인확인 수준은 BE 연동 후 결정·구현됩니다.</span>
          </div>
        </div>
      </Wrap>
    </PageContainer>
  );
};

export default ArchiveSignature;
